import React, { useState } from 'react';
import styled from 'styled-components';

const prompts = [
  {
    title: "Prompt‑on‑Demand",
    text: "I want to [task]. Please craft a ready‑to‑use ChatGPT prompt that sets the role, context, constraints and the exact output format."
  },
  {
    title: "Image‑to‑Text (OCR)",
    text: "(Upload image) 'Extract every legible word from this image, preserving original line breaks. Return plain text only—no commentary.'"
  },
  {
    title: "Clarifying Questions Booster",
    text: "I'm trying to [describe task]. Ask up to 10 precise questions whose answers will let you give me the most accurate help."
  },
  {
    title: "Expert Road‑Map",
    text: "Act as a [domain] expert with 10 years' experience. My goal is [goal]. After any clarifying questions, hand me a step‑by‑step action plan."
  },
  {
    title: "Brainstorming Coach",
    text: "I have a rough idea: [idea]. Ask up to 5 probing questions, then suggest 10 fresh angles or improvements."
  },
  {
    title: "Instant Meeting Minutes",
    text: "Here's a raw meeting transcript: [text] — distil it into: • a 5‑bullet summary • key decisions • action items with owner & deadline."
  },
  {
    title: "Explain at Five Levels",
    text: "Explain [concept] to (1) a child, (2) a teenager, (3) a college student, (4) a grad student and (5) an expert, each with suitable examples."
  },
  {
    title: "Tone‑Shift Rewriter",
    text: "Rewrite the text below so it sounds [desired tone e.g., friendly, formal, persuasive] to [audience], keeping length and meaning the same."
  },
  {
    title: "Customer Objection Simulator",
    text: "Role‑play as a skeptical customer considering [product/service]. List the 10 strongest objections you'd raise and how I might address each one."
  },
  {
    title: "Scroll‑Stopping Hooks",
    text: "Act as a [niche] copywriter. Generate 10 scroll‑stopping hooks (≤10 words) for a post about [topic] aimed at [audience]. Each hook should start with a number or 'Why/How/This'."
  }
];

const CarouselContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const PromptsRow = styled.div`
  display: flex;
  gap: 1.5rem;
  margin: 1.5rem 0;
`;
const PromptCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  padding: 1.5rem 1.2rem;
  min-width: 260px;
  max-width: 320px;
  min-height: 280px;
  height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
`;
const PromptTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #1976d2;
  font-size: 1.08rem;
  font-weight: 600;
`;
const PromptText = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  color: #222;
`;
const CopyButton = styled.button`
  background: #e3f2fd;
  color: #1976d2;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.2s;
  &:hover, &:focus {
    background: #bbdefb;
    outline: none;
  }
`;
const DotsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.6rem;
  margin-top: 1.2rem;
  min-height: 2.2rem;
`;
const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => (props.active ? '#1976d2' : '#e3e3e3')};
  transition: background 0.2s;
  cursor: pointer;
  padding: 0;
  outline: none;
  &:hover, &:focus {
    background: #90caf9;
  }
`;
const ArrowButton = styled.button`
  background: transparent;
  color: #1976d2;
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  margin: 0 0.5rem;
  &:hover:enabled, &:focus:enabled {
    background: #e3f2fd;
    color: #1565c0;
    outline: none;
  }
  &:disabled {
    color: #e3e3e3;
    cursor: not-allowed;
  }
`;

const FavouritePrompts = () => {
  const [start, setStart] = useState(0);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const visiblePrompts = prompts.slice(start, start + 3);

  const handleCopy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1200);
    } catch {}
  };

  return (
    <div>
      <h2 style={{ textAlign: 'center', color: 'var(--primary-color)', fontSize: '1.8rem', fontWeight: 700, marginBottom: '1.5rem' }}>My Favourite Prompts</h2>
      <CarouselContainer>
        <PromptsRow>
          {visiblePrompts.map((prompt, idx) => (
            <PromptCard key={prompt.title}>
              <PromptTitle>{prompt.title}</PromptTitle>
              <PromptText>{prompt.text}</PromptText>
              <CopyButton
                onClick={() => handleCopy(prompt.text, idx)}
                aria-label={`Copy prompt: ${prompt.title}`}
              >
                {copiedIdx === idx ? 'Copied!' : 'Copy'}
              </CopyButton>
            </PromptCard>
          ))}
        </PromptsRow>
        <DotsRow>
          <ArrowButton
            onClick={() => setStart(s => Math.max(0, s - 3))}
            disabled={start === 0}
            aria-label="Previous prompts"
          >
            ‹
          </ArrowButton>
          {Array.from({ length: Math.ceil(prompts.length / 3) }).map((_, i) => (
            <Dot
              key={i}
              active={start / 3 === i}
              aria-label={`Go to prompts set ${i + 1}`}
              onClick={() => setStart(i * 3)}
            />
          ))}
          <ArrowButton
            onClick={() => setStart(s => Math.min(prompts.length - 3, s + 3))}
            disabled={start >= prompts.length - 3}
            aria-label="Next prompts"
          >
            ›
          </ArrowButton>
        </DotsRow>
      </CarouselContainer>
    </div>
  );
};

export default FavouritePrompts; 